/**
 * `/v1/editions` — one årgang of a festival, and everything operational hangs on it.
 *
 * ---------------------------------------------------------------------------------------------
 * SIX CALLS SINCE QA-RUNDE 12 (#28a). This paragraph said for months that `DELETE` did not exist
 * because what removing an årgang should MEAN was undecided — it holds departments, tasks,
 * threads and performances. Atle decided: faresone nederst i «Rediger årgangen», rød knapp, and a
 * confirmation that only accepts the årgang's own name typed out. So there is a `DELETE`, it is a
 * SOFT delete on the server, and nothing in this module offers an undo for it, because there is no
 * restore route to honour one with. An archive endpoint still does not exist and is a different
 * question: `editionStatusSchema` carries `archived`, this module never mints it, and
 * `EditionFormModal` says why. The clone is the one call here that writes rows another domain owns.
 *
 * NEWEST FIRST, AND THAT IS AN INDEX RATHER THAN A PREFERENCE. `sort: 'year'` with
 * `direction: 'desc'` is the only order this list is ever asked in, because
 * `ix_editions__org_festival_year` is `(organization_id, festival_id, year DESC, id DESC)` and
 * the contract says out loud that a cursor which does not match that index is a cursor the query
 * plan cannot use. Somebody opening a festival in March is working on this year or planning the
 * next one; 2019 is history they scroll to.
 *
 * ONE PAGE, AND THE PAGE SAYS SO IF IT IS NOT THE WHOLE ANSWER. `MAX_PAGE_SIZE` is 100, and a
 * festival with more than a hundred årganger is a century old — so this asks for one page rather
 * than growing a "load more" nobody will ever press. It does NOT quietly drop the rest: the panel
 * renders a line saying the list is cut when `hasMore` comes back true, which is the one thing a
 * list must never leave unsaid (`TasksPage` made the same call for the same reason).
 *
 * THE FESTIVAL'S EDITION COUNT IS A SECOND READER OF THIS DATA. `festivalDtoSchema.editionCount`
 * is rendered in the festival page header and on every sidebar row, and it is computed by the
 * API — so creating an årgang has to invalidate the FESTIVAL keys as well as these. A create that
 * refreshed only the list it was made in would leave "3 årganger" under a list of four.
 * ---------------------------------------------------------------------------------------------
 */
import {
  cloneEditionResponseSchema,
  editionDtoSchema,
  editionListResponseSchema,
  MAX_PAGE_SIZE,
  type CloneEditionRequest,
  type CloneEditionResponse,
  type CreateEditionRequest,
  type EditionDto,
  type EditionListResponse,
  type UpdateEditionRequest,
} from '@tvitvi/shared';
import {
  queryOptions,
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { apiRequest } from '@/api/client';
import { CACHE } from '@/api/cache-tiers';
import { qk } from '@/api/query-keys';

const PATH = '/editions';

/**
 * Every årgang of one festival, newest first.
 *
 * List tier rather than reference tier: unlike departments and tags, this is the CONTENT of the
 * page somebody is looking at rather than a lookup every other list filters by, and a status that
 * moved from `planning` to `active` is exactly the kind of change a colleague makes while you
 * have the page open.
 *
 * No `status` parameter. `listEditionsQuerySchema` offers one and it can carry a single value —
 * there is no "everything except archived" — so sending one would hide three statuses to exclude
 * one. The panel receives every årgang the festival has and says what each of them is.
 */
export function editionsQueryOptions(orgId: string, festivalId: string | null) {
  return queryOptions({
    queryKey: qk.editionList(orgId, festivalId),
    queryFn: ({ signal }): Promise<EditionListResponse> =>
      apiRequest({
        path: PATH,
        query: {
          /*
           * `null` ASKS THE ORGANISATION'S QUESTION: every årgang it has, across festivals. That
           * is what the Tasks page's «arrangement» filter offers, and each row carries its
           * festival's name — `EditionSummary` has it — so the picker can say "God Nok 2027"
           * rather than "2027" and mean something in an organisation that runs three festivals.
           *
           * Spread rather than passed as `undefined`, for the reason every optional in this
           * codebase is: "the request carries no festival filter" is a property worth being able
           * to assert on the request.
           */
          ...(festivalId === null ? {} : { festivalId }),
          sort: 'year',
          direction: 'desc',
          limit: MAX_PAGE_SIZE,
        },
        schema: editionListResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

/**
 * One årgang, asked for by its own id — what the edition page is about.
 *
 * NOT PICKED OUT OF THE LIST ABOVE, and that is a decision rather than an oversight. The list is
 * one page of 100 ordered by year, and the edition page is an ADDRESS: somebody pastes it into
 * Slack, and it has to resolve on its own. Reading the subject out of a list would make the page
 * work or not depending on how many årganger the festival has and where in the order this one
 * fell — and a 404 would arrive as "this festival has no such year" rather than as a 404.
 *
 * The list is still asked for on that page, for the year control's siblings. Two requests, and
 * they fail independently: a festival whose list is down still shows the årgang you opened,
 * with its year on it, because the year comes from THIS answer.
 *
 * Detail tier, like every other single-object read: a status that moved from `planning` to
 * `active` is exactly the kind of change a colleague makes while you have the page open.
 */
export function editionQueryOptions(orgId: string, editionId: string) {
  return queryOptions({
    queryKey: qk.edition(orgId, editionId),
    queryFn: ({ signal }): Promise<EditionDto> =>
      apiRequest({ path: `${PATH}/${editionId}`, schema: editionDtoSchema, signal }),
    ...CACHE.detail,
  });
}

/**
 * A new årgang.
 *
 * `festivalId` is in the BODY rather than in the path, because that is where the contract puts
 * it — an edition belongs to a festival at birth and can never move (`updateEditionRequestSchema`
 * has no `festivalId`, deliberately).
 *
 * Two prefixes are invalidated, not one. See the header: the count on the festival and in the
 * sidebar is the second reader of this data.
 */
export function useCreateEdition(
  orgId: string,
): UseMutationResult<EditionDto, unknown, CreateEditionRequest> {
  return useMutation({
    mutationFn: (body: CreateEditionRequest) =>
      apiRequest({ path: PATH, method: 'POST', body, schema: editionDtoSchema }),
    /* `qk.participations`: «Hvem er med?» skriver deltakelser, og neste års forslag er et annet (QA13 punkt 32). */
    meta: { invalidates: [qk.editions(orgId), qk.festivals(orgId), qk.participations(orgId)] },
  });
}

export interface UpdateEditionInput {
  editionId: string;
  body: UpdateEditionRequest;
}

/**
 * Renaming, dating, moving zone, changing status — everything except which festival it is under.
 *
 * Only `qk.editions(orgId)` here: a PATCH cannot change how MANY årganger the festival has, so
 * the festival's own count and the sidebar are untouched by it. Invalidating them anyway would be
 * two requests for an answer that has not moved.
 */
export function useUpdateEdition(
  orgId: string,
): UseMutationResult<EditionDto, unknown, UpdateEditionInput> {
  return useMutation({
    mutationFn: ({ editionId, body }: UpdateEditionInput) =>
      apiRequest({
        path: `${PATH}/${editionId}`,
        method: 'PATCH',
        body,
        schema: editionDtoSchema,
      }),
    meta: { invalidates: [qk.editions(orgId)] },
  });
}

export interface CloneEditionInput {
  /** The årgang the structure is copied INTO. Never the source; that is in the body. */
  editionId: string;
  body: CloneEditionRequest;
}

/**
 * Copies a structure into an årgang — today the department tree, and nothing else.
 *
 * TWO PREFIXES, BECAUSE ONE CALL CHANGES TWO DOMAINS. The answer carries the target edition as
 * it now stands — `clonedFromEditionId` is set when the source was another årgang, and that is a
 * record of where this year came from that the panel is showing — so `qk.editions(orgId)` is not
 * optional the way it is for a read. And the call's whole purpose is to create DEPARTMENTS, so
 * `qk.departments(orgId)` goes with it: this is the one mutation in this module that writes rows
 * a different feature owns, which is exactly the case a single-prefix invalidation gets wrong.
 *
 * NOT `qk.tasks(orgId)`, although `useCreateDepartment` reaches for it. That one has to: creating
 * a department by an existing key RESTORES a soft-deleted one, and every task filed under it
 * comes back out of "Uten gruppe". A clone cannot move a task — it writes into the target
 * edition's own scope, under keys that are new there, and no task's `department` changes because
 * of it. Invalidating the task lists anyway would be a round trip for an answer that has not
 * moved, which is the habit the one-place rule exists to stop.
 *
 * NOT `qk.festivals(orgId)` either: `editionCount` cannot change, for the same reason a PATCH
 * leaves it alone.
 */
export function useCloneEdition(
  orgId: string,
): UseMutationResult<CloneEditionResponse, unknown, CloneEditionInput> {
  return useMutation({
    mutationFn: ({ editionId, body }: CloneEditionInput) =>
      apiRequest({
        path: `${PATH}/${editionId}/clone`,
        method: 'POST',
        body,
        schema: cloneEditionResponseSchema,
      }),
    meta: { invalidates: [qk.editions(orgId), qk.departments(orgId)] },
  });
}

/**
 * Faresonen på «Rediger årgangen» (#28a). 204, no body.
 *
 * TWO PREFIXES, UNLIKE THE PATCH RIGHT ABOVE IT. A rename cannot change how many years a festival
 * has and therefore leaves `festivalDtoSchema.editionCount` alone; a delete is precisely a change
 * to that number, so the sidebar row and the festival header have to be asked again or they keep
 * saying «3 årganger» over a list of two.
 *
 * NO UNDO IS OFFERED HERE, and that is honesty rather than an omission: the server soft-deletes,
 * but there is no restore route to call, so a toast with «Angre» on it would be a button that
 * cannot do what it says. That is exactly the line DESIGN.md §11.4 draws — a confirmation is for
 * what cannot be undone — and it is why this delete costs a typed name instead.
 */
export function useDeleteEdition(orgId: string): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    /*
     * Den slettede årgangens egne spørringer fjernes FØR invalideringen, ellers henter siden som
     * fortsatt står oppe raden og klarheten på nytt og får 404 i konsollen (QA5 C-L12).
     */
    mutationFn: async (editionId: string) => {
      await apiRequest({ path: `${PATH}/${editionId}`, method: 'DELETE' });
      queryClient.removeQueries({ queryKey: qk.edition(orgId, editionId) });
      queryClient.removeQueries({ queryKey: qk.editionReadiness(orgId, editionId) });
    },
    meta: { invalidates: [qk.editions(orgId), qk.festivals(orgId)] },
  });
}
